// VideoPlayer Component v1.0.0 - Single channel video with detection overlay
import { useRef, useEffect, useState, useCallback } from 'react'
import { useViewerStore } from '../stores/viewerStore'
import type { CameraChannel, VideoClip, Detection, BoundingBox } from '../types'
import { CAMERA_LABELS } from '../types'

interface VideoPlayerProps {
  channel: CameraChannel
  clip: VideoClip | null
  isPrimary?: boolean
  onTimeUpdate?: (time: number) => void
  onEnded?: () => void
  onClick?: () => void
}

export default function VideoPlayer({
  channel,
  clip,
  isPrimary = false,
  onTimeUpdate,
  onEnded,
  onClick,
}: VideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [isVideoReady, setIsVideoReady] = useState(false)

  const {
    isPlaying,
    playbackSpeed,
    showDetectionOverlay,
    maskChildDetections,
    checkMaskAuthorization,
    detectionFrames,
    showTimestamps,
    selectedDetectionId,
    setSelectedDetection,
  } = useViewerStore()

  // Get detection frame for this channel
  const detectionFrame = detectionFrames.get(channel)

  // Check if we have authorization to unmask children for this clip
  const maskAuth = clip ? checkMaskAuthorization(clip.id) : null
  const shouldMaskChildren = maskChildDetections && !maskAuth

  // Sync video playback with store
  useEffect(() => {
    if (!videoRef.current || !clip) return

    if (isPlaying && videoRef.current.paused) {
      videoRef.current.play().catch(() => {})
    } else if (!isPlaying && !videoRef.current.paused) {
      videoRef.current.pause()
    }
  }, [isPlaying, clip])

  // Sync playback speed
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = playbackSpeed
    }
  }, [playbackSpeed])

  // Handle video events
  const handleLoadedMetadata = useCallback(() => {
    if (videoRef.current) {
      setIsVideoReady(true)
    }
  }, [])

  const handleTimeUpdate = useCallback(() => {
    if (videoRef.current) {
      onTimeUpdate?.(videoRef.current.currentTime)
    }
  }, [onTimeUpdate])

  const handleEnded = useCallback(() => {
    onEnded?.()
  }, [onEnded])

  // Draw detection overlay on canvas
  useEffect(() => {
    if (!canvasRef.current || !containerRef.current || !showDetectionOverlay) return

    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // Match canvas to container size
    const rect = containerRef.current.getBoundingClientRect()
    canvas.width = rect.width
    canvas.height = rect.height

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height)

    if (!detectionFrame || !isVideoReady) return

    const { detections, plates, childMasks } = detectionFrame

    // Draw child masks FIRST (solid, obscuring)
    if (shouldMaskChildren && childMasks) {
      for (const mask of childMasks) {
        drawChildMask(ctx, mask, canvas.width, canvas.height)
      }
    }

    // Draw detection boxes
    for (const detection of detections) {
      // Skip children if masking (box would reveal their location)
      if (shouldMaskChildren && detection.isChild) continue

      const isSelected = detection.id === selectedDetectionId
      drawDetectionBox(ctx, detection, canvas.width, canvas.height, isSelected)
    }

    // Draw plate detections
    for (const plate of plates) {
      drawPlateBox(ctx, plate, canvas.width, canvas.height)
    }
  }, [detectionFrame, showDetectionOverlay, shouldMaskChildren, isVideoReady, selectedDetectionId])

  // Handle click on detection
  const handleCanvasClick = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (!canvasRef.current || !detectionFrame) return

      const rect = canvasRef.current.getBoundingClientRect()
      const x = (e.clientX - rect.left) / rect.width
      const y = (e.clientY - rect.top) / rect.height

      // Find clicked detection
      for (const detection of detectionFrame.detections) {
        if (shouldMaskChildren && detection.isChild) continue

        const box = detection.box
        if (x >= box.x && x <= box.x + box.width && y >= box.y && y <= box.y + box.height) {
          setSelectedDetection(detection.id === selectedDetectionId ? null : detection.id)
          return
        }
      }
      setSelectedDetection(null)
    },
    [detectionFrame, shouldMaskChildren, selectedDetectionId, setSelectedDetection]
  )

  return (
    <div
      ref={containerRef}
      className={`relative bg-black overflow-hidden ${isPrimary ? 'ring-2 ring-viewer-accent' : ''}`}
      onClick={onClick}
    >
      {/* Channel label */}
      <div className="absolute top-2 left-2 z-10 px-2 py-1 rounded bg-black/60 text-xs font-medium">
        {CAMERA_LABELS[channel]}
      </div>

      {/* Timestamp */}
      {showTimestamps && clip && (
        <div className="absolute top-2 right-2 z-10 px-2 py-1 rounded bg-black/60 text-xs font-mono">
          {new Date(clip.startTime).toLocaleTimeString()}
        </div>
      )}

      {/* Video element */}
      {clip ? (
        <video
          ref={videoRef}
          src={clip.url}
          className="w-full h-full object-contain"
          onLoadedMetadata={handleLoadedMetadata}
          onTimeUpdate={handleTimeUpdate}
          onEnded={handleEnded}
          muted={!isPrimary}
          playsInline
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-viewer-text-muted">
          <span>No footage</span>
        </div>
      )}

      {/* Detection overlay canvas */}
      {showDetectionOverlay && (
        <canvas
          ref={canvasRef}
          className="absolute inset-0 pointer-events-auto"
          onClick={handleCanvasClick}
        />
      )}

      {/* Child content warning */}
      {shouldMaskChildren && clip?.analysis?.hasChildDetection && (
        <div className="absolute bottom-2 left-2 z-10 px-2 py-1 rounded bg-detection-child/80 text-xs font-medium flex items-center gap-1">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clipRule="evenodd"
            />
          </svg>
          Protected Content
        </div>
      )}

      {/* Loading indicator */}
      {clip && !isVideoReady && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50">
          <div className="w-8 h-8 border-2 border-viewer-accent border-t-transparent rounded-full animate-spin" />
        </div>
      )}
    </div>
  )
}

// ============== Drawing functions ==============

function drawDetectionBox(
  ctx: CanvasRenderingContext2D,
  detection: Detection,
  canvasWidth: number,
  canvasHeight: number,
  isSelected: boolean
) {
  const { box, label, confidence, isChild } = detection

  const x = box.x * canvasWidth
  const y = box.y * canvasHeight
  const w = box.width * canvasWidth
  const h = box.height * canvasHeight

  // Color based on type
  let color: string
  if (isChild) {
    color = '#ff00ff' // Magenta for children
  } else if (label === 'person') {
    color = '#ff6b6b'
  } else if (label === 'vehicle') {
    color = '#ffd93d'
  } else if (label === 'animal') {
    color = '#6bcb77'
  } else {
    color = '#4d96ff'
  }

  // Draw box
  ctx.strokeStyle = color
  ctx.lineWidth = isSelected ? 3 : 2
  ctx.strokeRect(x, y, w, h)

  // Draw label background
  const labelText = `${label} ${Math.round(confidence * 100)}%`
  ctx.font = '12px Inter, sans-serif'
  const textMetrics = ctx.measureText(labelText)
  const labelWidth = textMetrics.width + 8
  const labelHeight = 18

  ctx.fillStyle = color
  ctx.fillRect(x, y - labelHeight, labelWidth, labelHeight)

  // Draw label text
  ctx.fillStyle = '#000'
  ctx.fillText(labelText, x + 4, y - 5)

  // Selection highlight
  if (isSelected) {
    ctx.strokeStyle = '#fff'
    ctx.lineWidth = 1
    ctx.setLineDash([4, 4])
    ctx.strokeRect(x - 2, y - 2, w + 4, h + 4)
    ctx.setLineDash([])
  }
}

function drawPlateBox(
  ctx: CanvasRenderingContext2D,
  plate: { plate: string; confidence: number; box: BoundingBox; isFlagged: boolean },
  canvasWidth: number,
  canvasHeight: number
) {
  const { box } = plate

  const x = box.x * canvasWidth
  const y = box.y * canvasHeight
  const w = box.width * canvasWidth
  const h = box.height * canvasHeight

  const color = plate.isFlagged ? '#ff3d00' : '#4d96ff'

  // Draw box
  ctx.strokeStyle = color
  ctx.lineWidth = 2
  ctx.strokeRect(x, y, w, h)

  // Draw plate text
  const labelText = plate.plate
  ctx.font = 'bold 14px JetBrains Mono, monospace'
  const textMetrics = ctx.measureText(labelText)
  const labelWidth = textMetrics.width + 8
  const labelHeight = 20

  ctx.fillStyle = color
  ctx.fillRect(x, y + h, labelWidth, labelHeight)

  ctx.fillStyle = '#fff'
  ctx.fillText(labelText, x + 4, y + h + 15)

  // Flagged indicator
  if (plate.isFlagged) {
    ctx.fillStyle = '#ff3d00'
    ctx.beginPath()
    ctx.arc(x + w, y, 6, 0, Math.PI * 2)
    ctx.fill()
  }
}

function drawChildMask(
  ctx: CanvasRenderingContext2D,
  box: BoundingBox,
  canvasWidth: number,
  canvasHeight: number
) {
  const x = box.x * canvasWidth
  const y = box.y * canvasHeight
  const w = box.width * canvasWidth
  const h = box.height * canvasHeight

  // Solid mask with slight transparency
  ctx.fillStyle = 'rgba(40, 40, 40, 0.95)'
  ctx.fillRect(x, y, w, h)

  // Add blur effect (simulated with multiple overlapping rects)
  for (let i = 0; i < 3; i++) {
    const expand = i * 5
    ctx.fillStyle = `rgba(40, 40, 40, ${0.3 - i * 0.1})`
    ctx.fillRect(x - expand, y - expand, w + expand * 2, h + expand * 2)
  }

  // Draw "PROTECTED" text
  ctx.fillStyle = '#ff00ff'
  ctx.font = 'bold 14px Inter, sans-serif'
  ctx.textAlign = 'center'
  ctx.fillText('PROTECTED', x + w / 2, y + h / 2)
  ctx.textAlign = 'left'

  // Draw lock icon (simple shape)
  const iconX = x + w / 2
  const iconY = y + h / 2 + 20
  ctx.strokeStyle = '#ff00ff'
  ctx.lineWidth = 2
  ctx.beginPath()
  ctx.arc(iconX, iconY - 5, 8, Math.PI, 0)
  ctx.stroke()
  ctx.fillStyle = '#ff00ff'
  ctx.fillRect(iconX - 10, iconY, 20, 14)
}
